import { HierarcicalObjectReference } from "@novorender/data-js-api";

import { Node, Tree } from "components/objectTree/types";
import { ModelTreeNodeData } from "features/modelTree/types";
import { NodeType } from "types/misc";
import { decodeObjPathName, getParentPath } from "utils/objectData";
import { isLeafNode } from "utils/objectPath";

/**
 * A node collapsed with its children unloaded, so expanding it fetches its full real
 * hierarchy — even for a folder match. {@link buildFilteredTree} opens the ones it links
 * children under.
 */
export function createNodeFromObject(obj: HierarcicalObjectReference): Node<ModelTreeNodeData> {
    return {
        path: obj.path,
        name: decodeObjPathName(obj.path.split("/").at(-1)!),
        expanded: false,
        loading: false,
        isLeaf: isLeafNode(obj),
        children: undefined,
        hasMoreChildren: false,
        data: { object: obj },
    };
}

/**
 * Builds the search-filtered tree from {@link ObjectDataAccess.getFilteredTree}'s rows.
 *
 * The result contains only the rows the caller passes in — matches and the ancestors
 * connecting them to the root. Files with no match are not added. A node with rows
 * linked beneath it is expanded with only those children, and keeps `hasMoreChildren`
 * so expanding a match later loads its real children on demand. Any other node stays
 * collapsed with unloaded children. A row whose parent is missing is left unlinked,
 * so it and everything beneath it are unreachable.
 *
 * @param objects Matches and the ancestors connecting them, keyed by path; the first
 *   row per path wins.
 */
export function buildFilteredTree(objects: HierarcicalObjectReference[]): Tree<ModelTreeNodeData> {
    const filteredTree: Tree<ModelTreeNodeData> = {
        "": {
            path: "",
            name: "Root",
            expanded: true,
            loading: false,
            isLeaf: false,
            children: [],
            hasMoreChildren: true,
            data: {
                object: { id: -1, path: "", type: NodeType.Internal } as HierarcicalObjectReference,
            },
        },
    };

    for (const obj of objects) {
        filteredTree[obj.path] ??= createNodeFromObject(obj);
    }

    for (const path of Object.keys(filteredTree)) {
        if (path === "") {
            continue;
        }
        const parent = filteredTree[getParentPath(path)];
        if (!parent) {
            continue;
        }
        if (!parent.children) {
            parent.expanded = true;
            parent.children = [];
            parent.hasMoreChildren = true;
        }
        parent.children.push(path);
    }

    return filteredTree;
}
